// src/components/ProtectedRoute.js

import React, { useEffect, useState } from 'react';
import { useMsal } from "@azure/msal-react";
import { Navigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { fetchUserRoles } from '../../services/authService';
import SecureStorageService from '../../services/secureStorageService';

const ProtectedRoute = ({ children, roles }) => {
  const { t } = useTranslation('general');
  const { accounts, instance } = useMsal();
  const [hasRequiredRole, setHasRequiredRole] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkRoles = async () => {
      if (accounts && accounts.length > 0) {
        try {
          // First try to get roles from SecureStorageService (encrypted, session-based)
          let userRoles = SecureStorageService.getRoles() || [];
          
          // If no roles in secure storage, fetch them from API
          if (!userRoles.length) {
            const activeAccount = instance.getActiveAccount();
            if (activeAccount) {
              userRoles = await fetchUserRoles(instance, activeAccount);
              SecureStorageService.setRoles(userRoles);
            }
          }
          
          // Check if user has required role
          const hasRole = roles.some(role => userRoles.includes(role));
          setHasRequiredRole(hasRole);
        } catch (error) {
          console.error('Error checking roles:', error);
          setHasRequiredRole(false);
        }
      }
      setIsLoading(false);
    };

    checkRoles();
  }, [accounts, instance, roles]);

  if (!accounts || accounts.length === 0) {
    return <Navigate to="/not-authorized" replace />;
  }

  if (isLoading) {
    return <Box sx={{ padding: { xs: 3, sm: 3 }, textAlign: 'center' }}>
      <Typography sx={{ fontSize: { xs: '1rem', sm: '1rem' } }}>
        {t('protectedRoute.loading')}
      </Typography>
    </Box>;
  }

  if (!hasRequiredRole) {
    return (
      <Box sx={{ 
        padding: { xs: 3, sm: 3 }, 
        textAlign: 'center',
        maxWidth: 600,
        mx: 'auto',
        mt: { xs: 4, sm: 6 }
      }}>
        <Typography 
          variant="h4" 
          color="error"
          sx={{ 
            fontSize: { xs: '1.5rem', sm: '2.125rem' },
            mb: 2,
            fontWeight: 600,
          }}
        >
          {t('protectedRoute.accessDeniedTitle')}
        </Typography>
        <Typography 
          sx={{ 
            fontSize: { xs: '1rem', sm: '1rem' },
            lineHeight: 1.6,
            color: 'text.secondary',
          }}
        >
          {t('protectedRoute.accessDeniedDescription')}
        </Typography>
      </Box>
    );
  }

  return children;
};

export default ProtectedRoute;